import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCreateAddress, useUpdateAddress } from '@/hooks/useAddressActions';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Address } from '@octave/contracts';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const addressFormSchema = z.object({
  name: z.string().optional(),
  organizationId: z.string().min(1, "L'organisation est requise"),
  street: z.string().min(1, 'La rue est requise'),
  city: z.string().min(1, 'La ville est requise'),
  postalCode: z.string().min(1, 'Le code postal est requis'),
  country: z.string().min(1, 'Le pays est requis'),
  isPersonal: z.boolean().default(false),
  companyName: z.string().optional(),
  recipientName: z.string().optional(),
  email: z.string().email('Email invalide').min(1, "L'email est requis"),
  phone: z.string().min(1, 'Le numéro de téléphone est requis'),
});

type AddressFormValues = z.infer<typeof addressFormSchema>;

interface AddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits this address instead of creating one. */
  address?: Address;
}

export function AddressDialog({
  open,
  onOpenChange,
  address,
}: AddressDialogProps) {
  const { data: organization } = useCurrentOrganization();

  if (!organization) {
    return null;
  }

  return (
    <AddressDialogContent
      open={open}
      onOpenChange={onOpenChange}
      address={address}
      organizationId={organization.id}
    />
  );
}

interface AddressDialogContentProps extends AddressDialogProps {
  organizationId: string;
}

function AddressDialogContent({
  open,
  onOpenChange,
  address,
  organizationId,
}: AddressDialogContentProps) {
  const { mutate: createAddress, isPending: isCreating } = useCreateAddress();
  const { mutate: updateAddress, isPending: isUpdating } = useUpdateAddress(
    address?.id,
  );
  const isPending = isCreating || isUpdating;

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: {
      name: address?.name || '',
      organizationId,
      street: address?.street || '',
      city: address?.city || '',
      postalCode: address?.zipCode || '',
      country: address?.country || '',
      isPersonal: address?.isPersonal ?? false,
      companyName: address?.companyName || '',
      recipientName: address?.recipientName || '',
      email: address?.email || '',
      phone: address?.phone || '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: AddressFormValues) => {
    const onSuccess = () => onOpenChange(false);
    if (address) {
      updateAddress(
        {
          name: data.name || undefined,
          street: data.street,
          zipCode: data.postalCode,
          city: data.city,
          country: data.country,
          isPersonal: data.isPersonal,
          companyName: !data.isPersonal ? data.companyName : null,
          recipientName: data.recipientName || null,
          email: data.email,
          phone: data.phone,
        },
        { onSuccess },
      );
    } else {
      createAddress(
        {
          name: data.name || null,
          street: data.street,
          zipCode: data.postalCode,
          city: data.city,
          country: data.country,
          organizationId,
          isPersonal: data.isPersonal,
          companyName: !data.isPersonal ? data.companyName : null,
          recipientName: data.recipientName || null,
          email: data.email,
          phone: data.phone,
        },
        { onSuccess },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            {address ? "Modifier l'adresse" : 'Nouvelle adresse'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Nom de l'adresse{' '}
                        <span className="text-slate-400 text-xs">
                          (optionnel)
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: Domicile, Bureau..."
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="recipientName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Nom du destinataire{' '}
                        <span className="text-slate-400 text-xs">
                          (optionnel)
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex: Jean Dupont"
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="space-y-4">
              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-slate-600">
                      Rue
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="123 rue de la Paix"
                        className="bg-white border-slate-200"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Ville
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Paris"
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Code postal
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="75000"
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Pays
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="France"
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Type d'adresse */}
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="isPersonal"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        Adresse personnelle
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-slate-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Une livraison vers une adresse personnelle entraîne
                            des frais supplémentaires de 3€
                          </TooltipContent>
                        </Tooltip>
                      </FormLabel>
                      <div className="text-xs text-slate-500">
                        Cette adresse est-elle une adresse personnelle ?
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {form.watch('isPersonal') && (
                <div className="flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 border border-amber-200">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    Une livraison vers cette adresse personnelle entraînera des
                    frais supplémentaires de 3€
                  </span>
                </div>
              )}

              {!form.watch('isPersonal') && (
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Nom de l'entreprise
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Nom de votre entreprise"
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Suivi de commande */}
            <div className="space-y-4 bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Informations pour le suivi de commande
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Ces informations sont nécessaires pour vous tenir informé de
                  l'état de votre commande
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="exemple@email.com"
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-slate-600">
                        Numéro de téléphone
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="+33 6 12 34 56 78"
                          className="bg-white border-slate-200"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-slate-200"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={
                  isPending ||
                  !form.formState.isValid ||
                  !form.formState.isDirty
                }
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {address ? 'Mise à jour en cours...' : 'Ajout en cours...'}
                  </>
                ) : address ? (
                  "Mettre à jour l'adresse"
                ) : (
                  "Ajouter l'adresse"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
